import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, RefreshCcw } from "lucide-react";
import { Modal, Button } from "react-bootstrap";
import MyContext from "../../../MyContext";
import "../Inventory/InventoryAccordion.css";

const SyncItemsInstamartAdmin = ({ userID }) => {
  const { config } = useContext(MyContext);

  const [storesLoading, setStoresLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [activeStoreId, setActiveStoreId] = useState("");
  const [syncItemsData, setSyncItemsData] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionStatus, setActionStatus] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  const activeStore = useMemo(() => {
    return stores.find((store) => String(store.fk_userid) === String(activeStoreId)) || null;
  }, [stores, activeStoreId]);

  const fetchStores = async () => {
    try {
      setStoresLoading(true);

      const response = await axios.post(
        `https://api.anlook.com/api/Order/v2.0/getStoresInstamart`,
        {},
        config
      );

      if (response?.data?.responseCode === "00") {
        const storeList = response?.data?.response || [];
        setStores(storeList);

        if (storeList.length > 0) {
          setActiveStoreId(String(storeList[0].fk_userid));
        } else {
          setActiveStoreId("");
        }
      } else {
        setStores([]);
        setActiveStoreId("");
      }
    } catch (error) {
      console.error("getStoresInstamart API error:", error);
      setStores([]);
      setActiveStoreId("");
    } finally {
      setStoresLoading(false);
    }
  };

  const fetchStoreItems = async (fkUserId, preserveScroll = false) => {
    if (!fkUserId) {
      setSyncItemsData([]);
      return;
    }
    
    const currentScrollY = preserveScroll ? window.scrollY || 0 : null;

    try {
      setItemsLoading(true);

      const response = await axios.post(
        `https://api.anlook.com/api/Order/v2.0/getSyncItemsInstamartAdmin?fk_userid=${fkUserId}`,
       {},
        config
      );

      if (response?.data?.responseCode === "00") {
        setSyncItemsData(response?.data?.response || []);
      } else {
        setSyncItemsData([]);
      }
    } catch (error) {
      console.error("getSyncItemsInstamartAdmin API error:", error);
      setSyncItemsData([]);
    } finally {
      setItemsLoading(false);

      if (preserveScroll && currentScrollY !== null) {
        window.scrollTo({ top: currentScrollY, behavior: "auto" });
      }
    }
  };

  useEffect(() => {
    fetchStores();
  }, [userID]);

  useEffect(() => {
    if (activeStoreId) {
      setSelectedItems({});
      fetchStoreItems(activeStoreId);
    }
  }, [activeStoreId]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return syncItemsData;

    return syncItemsData
      .map((group) => {
        const filteredItems = (group.item_list || []).filter((item) => {
          return (
            String(group.message || "").toLowerCase().includes(q) ||
            String(item.item_name || "").toLowerCase().includes(q) ||
            String(item.item_id || "").toLowerCase().includes(q) ||
            String(item.size || "").toLowerCase().includes(q)
          );
        });

        return {
          ...group,
          item_list: filteredItems,
        };
      })
      .filter((group) => (group.item_list || []).length > 0);
  }, [syncItemsData, search]);

  const toggleItem = (itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const toggleGroupItems = (group) => {
    const items = group.item_list || [];
    const allSelected =
      items.length > 0 && items.every((item) => selectedItems[item.item_id]);

    const updated = { ...selectedItems };

    items.forEach((item) => {
      updated[item.item_id] = !allSelected;
    });

    setSelectedItems(updated);
  };

  const allVisibleItems = useMemo(() => {
    return filteredGroups.flatMap((group) => group.item_list || []);
  }, [filteredGroups]);

  const selectedList = useMemo(() => {
    return allVisibleItems.filter((item) => selectedItems[item.item_id]);
  }, [allVisibleItems, selectedItems]);

  const hasSelectedItems = selectedList.length > 0;

  const handleOpenConfirm = (status) => {
    if (!hasSelectedItems || !activeStoreId) return;
    setActionStatus(status);
    setShowConfirmModal(true);
  };

  const handleSubmitStatus = async () => {
    if (!activeStoreId) return;

    try {
      setSubmitLoading(true);

      const payload = {
        fk_userid: String(activeStoreId),
        status: actionStatus,
        itemlist: selectedList.map((item) => ({
          itemid: String(item.item_id),
        })),
      };

      const response = await axios.post(
        `https://api.anlook.com/api/Order/UpdateMenuItemAdmin`,
        payload,
        config
      );

      console.log("Store wise status update response:", response.data);

      setShowConfirmModal(false);
      setSelectedItems({});
      await fetchStoreItems(activeStoreId, true);
    } catch (error) {
      console.error("SyncCatalogItemInstamart API error:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="inv-industrial-wrap">
      <div className="inv-industrial-head">
        <div className="inv-industrial-title">
          <h2 className="CreateCampaign">Instamart Store Items</h2>
        </div>

        <div
          className="inv-industrial-actions"
          style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}
        >
          <div className="inv-industrial-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search group / item / item id / size..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            className="inv-btn inv-btn-ghost"
            onClick={() => {
              fetchStores();
              if (activeStoreId) fetchStoreItems(activeStoreId, true);
            }}
            title="Refresh"
            type="button"
          >
            <RefreshCcw size={16} />
          </button>

          <button
            type="button"
            className="btn btn-success"
            disabled={!hasSelectedItems || !activeStoreId}
            onClick={() => handleOpenConfirm("Y")}
          >
            Set Active
          </button>

          <button
            type="button"
            className="btn btn-danger"
            disabled={!hasSelectedItems || !activeStoreId}
            onClick={() => handleOpenConfirm("N")}
          >
            Set Inactive
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "18px",
        }}
      >
        {storesLoading ? (
          <div className="inv-industrial-card" style={{ padding: "14px 18px" }}>
            Loading stores...
          </div>
        ) : stores.length === 0 ? (
          <div className="inv-industrial-card" style={{ padding: "14px 18px" }}>
            No stores found.
          </div>
        ) : (
          stores.map((store) => {
            const isActive = String(activeStoreId) === String(store.fk_userid);

            return (
              <button
                key={store.fk_userid}
                type="button"
                onClick={() => setActiveStoreId(String(store.fk_userid))}
                style={{
                  border: isActive ? "1px solid #01c37d" : "1px solid #e5e7eb",
                  background: isActive ? "#ecfdf5" : "#ffffff",
                  color: isActive ? "#065f46" : "#111827",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  minWidth: "180px",
                  textAlign: "left",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: "14px" }}>
                  {store.organization_name || "Unnamed Store"}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Store ID: {store.fk_userid}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                  Total Items: {store.total_items || "0"}
                </div>
              </button>
            );
          })
        )}
      </div>

      {activeStore && (
        <div
          className="inv-industrial-card"
          style={{
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Selected Store</div>
            <div style={{ fontWeight: 700, color: "#111827" }}>
              {activeStore.organization_name}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>fk_userid</div>
            <div style={{ fontWeight: 700, color: "#111827" }}>{activeStore.fk_userid}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>Total Items</div>
            <div style={{ fontWeight: 700, color: "#111827" }}>{activeStore.total_items}</div>
          </div>
        </div>
      )}

      {itemsLoading ? (
        <div className="inv-loading-wrap">
          <div className="inv-loading-card">
            <div className="inv-spinner" />
            <div className="inv-loading-text">Loading store items…</div>
          </div>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="inv-industrial-card">
          <div className="inv-industrial-empty">No items found for this store.</div>
        </div>
      ) : (
        filteredGroups.map((group, index) => {
          const items = group.item_list || [];
          const selectedCount = items.filter((item) => selectedItems[item.item_id]).length;

          return (
            <div
              className="inv-industrial-card"
              key={`${group.id || group.message}-${index}`}
              style={{ marginBottom: "20px" }}
            >
              <div
                style={{
                  padding: "16px 18px",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {group.message || "Store Items"}
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    {items.length} items • {selectedCount} selected
                  </p>
                </div>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: items.length ? "pointer" : "not-allowed",
                    opacity: items.length ? 1 : 0.5,
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={!items.length}
                    checked={
                      items.length > 0 &&
                      items.every((item) => selectedItems[item.item_id])
                    }
                    onChange={() => toggleGroupItems(group)}
                  />
                  Select all
                </label>
              </div>

              <div className="inv-industrial-table">
                <div className="inv-industrial-row inv-industrial-headrow">
                  <div>Select</div>
                  <div>Image</div>
                  <div>Name</div>
                  <div>Item ID</div>
                  <div>Size</div>
                  <div>Price</div>
                  <div>Stock</div>
                  <div>Status</div>
                </div>

                {items.map((item) => {
                  const isActive = String(item?.inStock || "N") === "Y";

                  return (
                    <div
                      className="inv-industrial-row"
                      key={`${item.item_id}-${item.size_id || item.pk_item_id}`}
                    >
                      <div>
                        <input
                          type="checkbox"
                          checked={!!selectedItems[item.item_id]}
                          onChange={() => toggleItem(item.item_id)}
                        />
                      </div>

                      <div className="inv-industrial-imgcell">
                        {item?.photo ? (
                          <img
                            src={item.photo}
                            alt={item.item_name}
                            className="inv-industrial-thumb"
                          />
                        ) : (
                          <div className="inv-industrial-thumb ph">No Img</div>
                        )}
                      </div>

                      <div className="inv-industrial-name">
                        <div className="inv-industrial-name-main">
                          {item.item_name || "—"}
                        </div>
                        <div className="inv-industrial-name-sub">
                          {item.item_description || "No description"}
                        </div>
                      </div>

                      <div>{item.item_id || "—"}</div>
                      <div>{item.size || "—"}</div>
                      <div>₹{item.price || "0"}</div>
                      <div>{item.stock || "0"}</div>

                      <div>
                        <span
                          style={{
                            background: isActive ? "#dcfce7" : "#fee2e2",
                            color: isActive ? "#166534" : "#991b1b",
                            borderRadius: "999px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <Modal
        show={showConfirmModal}
        onHide={() => !submitLoading && setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton={!submitLoading}>
          <Modal.Title>
            {actionStatus === "Y" ? "Confirm Set Active" : "Confirm Set Inactive"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={{ marginBottom: "12px" }}>
            Are you sure you want to{" "}
            <strong>{actionStatus === "Y" ? "set active" : "set inactive"}</strong>{" "}
            these selected items for{" "}
            <strong>{activeStore?.organization_name || "this store"}</strong>?
          </p>

          <div
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "10px",
              background: "#f9fafb",
            }}
          >
            {selectedList.map((item) => (
              <div
                key={`${item.item_id}-${item.size_id || item.pk_item_id}`}
                style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div style={{ fontWeight: 600 }}>{item.item_name}</div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                  Item ID: {item.item_id}
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
            disabled={submitLoading}
          >
            Cancel
          </Button>

          <Button
            variant={actionStatus === "Y" ? "success" : "danger"}
            onClick={handleSubmitStatus}
            disabled={submitLoading || !hasSelectedItems || !activeStoreId}
          >
            {submitLoading
              ? "Updating..."
              : actionStatus === "Y"
                ? "Confirm Active"
                : "Confirm Inactive"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SyncItemsInstamartAdmin;